import React, { useCallback, useEffect, useState } from "react";
import { RefreshCw, Truck, Copy, MapPin, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import { api } from "../api/client";

const fmt = (s) => {
    if (!s) return "—";
    try { return new Date(s).toLocaleString("en-IN", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }); }
    catch { return s; }
};

const Tracking = ({ orderId, autoRefresh = true, dense = false }) => {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [refreshing, setRefreshing] = useState(false);

    const load = useCallback(async () => {
        try {
            const r = await api.get(`/orders/${orderId}/tracking`);
            setData(r.data);
        } catch {
            /* silent */
        } finally {
            setLoading(false);
            setRefreshing(false);
        }
    }, [orderId]);

    useEffect(() => { load(); }, [load]);

    useEffect(() => {
        if (!autoRefresh) return;
        const t = setInterval(() => { setRefreshing(true); load(); }, 60_000);
        return () => clearInterval(t);
    }, [autoRefresh, load]);

    if (loading) {
        return <div className="h-32 skeleton rounded-2xl" />;
    }
    if (!data || !data.awb_code) {
        return (
            <div className={`rounded-2xl border border-dashed border-zinc-200 bg-zinc-50/60 p-4 text-center text-xs text-zinc-500 dark:border-white/10 dark:bg-white/5`} data-testid="tracking-empty">
                <Truck className="mx-auto mb-2 h-5 w-5 text-zinc-400" />
                Tracking will appear here once the order is dispatched.
            </div>
        );
    }

    const isShiprocket = data.source === "shiprocket";

    return (
        <div className="rounded-2xl border border-zinc-100 bg-white p-4 shadow-soft dark:border-white/5 dark:bg-zinc-900" data-testid="tracking-section">
            <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                    <p className="text-xs uppercase tracking-wide text-zinc-400">{isShiprocket ? "Live tracking" : "Manual tracking"}</p>
                    <p className="mt-1 text-sm font-semibold text-zinc-900 dark:text-zinc-50">{data.courier_name || "Courier"}</p>
                    <div className="mt-1 flex items-center gap-1.5 text-xs text-zinc-500">
                        <span className="font-mono">{data.awb_code}</span>
                        <button
                            onClick={() => { navigator.clipboard?.writeText(data.awb_code); toast.success("AWB copied"); }}
                            className="rounded p-1 hover:bg-zinc-100 dark:hover:bg-white/5"
                            aria-label="Copy AWB"
                            data-testid="tracking-copy-awb"
                        >
                            <Copy className="h-3 w-3" />
                        </button>
                        {isShiprocket && (
                            <a
                                href={`https://shiprocket.co/tracking/${data.awb_code}`}
                                target="_blank" rel="noreferrer"
                                className="ml-2 inline-flex items-center gap-1 text-[11px] text-brand-700 hover:underline"
                            >
                                Open <ExternalLink className="h-3 w-3" />
                            </a>
                        )}
                    </div>
                    {data.tracking_status && (
                        <span className="mt-2 inline-block rounded-full bg-brand-50 px-2 py-0.5 text-[10px] font-semibold uppercase text-brand-700 dark:bg-brand-500/15 dark:text-brand-300">{data.tracking_status}</span>
                    )}
                </div>
                <button
                    onClick={() => { setRefreshing(true); load(); }}
                    disabled={refreshing}
                    className="inline-flex items-center gap-1.5 rounded-full border border-zinc-200 px-3 py-1.5 text-xs font-medium text-zinc-700 hover:border-brand-300 hover:text-brand-700 disabled:opacity-50 dark:border-white/10 dark:text-zinc-300"
                    data-testid="tracking-refresh"
                >
                    <RefreshCw className={`h-3 w-3 ${refreshing ? "animate-spin" : ""}`} /> Refresh
                </button>
            </div>
            {(data.events && data.events.length > 0) ? (
                <ol className="mt-4 space-y-3">
                    {data.events.slice(0, dense ? 5 : 30).map((e, i) => (
                        <li key={i} className="flex items-start gap-3 text-xs" data-testid={`tracking-event-${i}`}>
                            <span className={`mt-1 h-2 w-2 shrink-0 rounded-full ${i === 0 ? "bg-brand-600 ring-2 ring-brand-200" : "bg-zinc-300 dark:bg-white/20"}`} />
                            <div className="flex-1">
                                <p className="font-medium text-zinc-900 dark:text-zinc-50">{e.status || e.description}</p>
                                <p className="text-zinc-500">
                                    {e.location && <span>{e.location} • </span>}
                                    {fmt(e.timestamp)}
                                </p>
                            </div>
                        </li>
                    ))}
                </ol>
            ) : (
                <p className="mt-3 text-xs text-zinc-500">No tracking events yet. Pickup scheduled.</p>
            )}
        </div>
    );
};

export default Tracking;
